import * as React from 'react';
import { useHistory } from 'react-router';

import styled from '@emotion/styled';

import { COLORS, FontWeight, transparentize } from '@clutter/clean';

import { TextButton } from '@portal/components/shared/customer_protected/text_button';
import {
  DetailedBannerAlertContainer,
  DetailedBannerAlertHeading,
  DetailedBannerAlertSubheading,
} from '@portal/components/shared/detailed_banner_alert';
import { itemsWithConditionNotesURL } from '@portal/config/routes';

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 16px;
  height: 16px;
  padding: 0;
  margin: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  line-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/**
 * `customer_protected/TextButton` does not forward `className` to the underlying Clean button (fragment wrapper),
 * so `styled(TextButton)` never colors the CTA. Scope teal on the real `button` inside instead; beats parent
 * `DetailedBannerAlertContainer`’s `button { color: toucan }` via extra class in the selector.
 */
const ViewItemsCta = styled.div`
  button {
    color: ${COLORS.tealPrimary};
    &:hover {
      color: ${COLORS.tealDark};
    }
  }
`;

/**
 * Portal inventory page banner when `itemsWithUnacknowledgedConditionNotesCount` is positive.
 * Typography and spacing follow `DetailedBannerAlert`; background stays the pre-PED gray from design (`22:357`).
 */
const ConditionNotesBannerContainer = styled(DetailedBannerAlertContainer)`
  background: #f0f0f0;

  ${CloseButton} {
    color: ${COLORS.panther};
    &:hover {
      color: ${transparentize(COLORS.panther, 0.45)};
    }
  }
`;

const BannerMainText = styled.div`
  padding-right: 28px;
  box-sizing: border-box;
  width: 100%;
`;

export const AccountConditionNotesBanner: React.FC<{ count: number }> = ({ count }) => {
  const [dismissed, setDismissed] = React.useState(false);
  const history = useHistory();

  if (count <= 0 || dismissed) return null;

  return (
    <ConditionNotesBannerContainer>
      <CloseButton type="button" onClick={() => setDismissed(true)} aria-label="Dismiss">
        <svg viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <path d="M1 1L7 7M7 1L1 7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      </CloseButton>

      <BannerMainText>
        <DetailedBannerAlertHeading weight={FontWeight.Medium}>
          Review the item conditions from your appointment
        </DetailedBannerAlertHeading>
        <DetailedBannerAlertSubheading>
          Our team noted conditions on certain items. You can view the documented items here anytime.
        </DetailedBannerAlertSubheading>
      </BannerMainText>

      <ViewItemsCta>
        <TextButton onClick={() => history.push(itemsWithConditionNotesURL())}>View items</TextButton>
      </ViewItemsCta>
    </ConditionNotesBannerContainer>
  );
};
